import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import RoleAwareLayout from "../../components/layouts/RoleAwareLayout";
import { getStripePromise } from "../../utils/stripeLoader";
import { getSubscriptionAccessView } from "../../utils/subscriptionAccessState";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;
const stripePromise = getStripePromise(API_BASE_URL);

// Payment Form Component (uses Stripe hooks)
const PaymentForm = ({ userType, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [setupIntent, setSetupIntent] = useState(null);

  useEffect(() => {
    createSetupIntent();
  }, []);

  const createSetupIntent = async () => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/setup-intent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSetupIntent(response.data.setupIntentClientSecret);
    } catch (error) {
      setError("Failed to initialize payment form. Please try again.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !setupIntent) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Confirm the setup intent with Stripe
      const { error: confirmError, setupIntent: confirmedSetupIntent } =
        await stripe.confirmCardSetup(setupIntent, {
          payment_method: {
            card: cardElement,
          },
        });

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
        return;
      }

      // Save the payment method to our backend
      const token = localStorage.getItem("ogestyle_token");
      await axios.post(
        `${API_BASE_URL}/api/subscription-new/confirm-payment`,
        {
          setupIntentId: confirmedSetupIntent.id,
          paymentMethodId: confirmedSetupIntent.payment_method,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onSuccess();
    } catch (error) {
      setError(
        error.response?.data?.error ||
          "Failed to save payment method. Please try again.",
      );
      setProcessing(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: "16px",
        color: "#374151",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        "::placeholder": {
          color: "#9ca3af",
        },
      },
      invalid: {
        color: "#dc2626",
        iconColor: "#dc2626",
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "16px",
        }}
      >
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "8px",
          }}
        >
          💳 Card Information
        </label>
        <div
          style={{
            background: "white",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            padding: "12px",
          }}
        >
          <CardElement options={cardStyle} />
        </div>
      </div>

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            color: "#dc2626",
            fontSize: "14px",
          }}
        >
          ⚠️ {error}
        </div>
      )}

      <div
        style={{
          background: "#eff6ff",
          border: "1px solid #dbeafe",
          borderRadius: "8px",
          padding: "12px",
          marginBottom: "16px",
          fontSize: "13px",
          color: "#1e40af",
        }}
      >
        🔒 Your payment information is secured by Stripe. We never store your
        card details on our servers.
      </div>

      <div style={{ display: "flex", gap: "12px" }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          style={{
            flex: 1,
            padding: "14px",
            background: "#f3f4f6",
            color: "#374151",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: "500",
            cursor: processing ? "not-allowed" : "pointer",
            opacity: processing ? 0.6 : 1,
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          style={{
            flex: 2,
            padding: "14px",
            background: processing ? "#9ca3af" : "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: "600",
            cursor: !stripe || processing ? "not-allowed" : "pointer",
          }}
        >
          {processing ? "Processing..." : "💳 Save Payment Method"}
        </button>
      </div>
    </form>
  );
};

// Main Component
const PaymentMethodSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userType, setUserType] = useState("business");
  const [isUpdate, setIsUpdate] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionData();
  }, [location]);

  const fetchSubscriptionData = async () => {
    const token = localStorage.getItem("ogestyle_token");

    // Determine user type from path
    const path = location.pathname;
    let type = "business";
    if (path.includes("/freelancer")) {
      type = "freelancer";
    }
    setUserType(type);

    // Check if this is an update
    const params = new URLSearchParams(location.search);
    setIsUpdate(params.get("update") === "true");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/subscription-new/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSubscription(response.data);

      // Redirect to checkout preview if trial expired
      if (!response.data.isInTrial) {
        const basePath =
          type === "freelancer"
            ? "/dashboard/freelancer/subscription"
            : "/dashboard/settings/subscription/checkout";
        navigate(basePath);
        return;
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    // accessState decides whether a checkout still has to happen (utils/subscriptionAccessState.js).
    const accessView = getSubscriptionAccessView(subscription);
    const requiresSubscriptionPayment = accessView.action === "checkout";

    if (requiresSubscriptionPayment && !isUpdate) {
      const checkoutPath =
        userType === "freelancer"
          ? "/dashboard/freelancer/subscription/checkout"
          : "/dashboard/settings/subscription/checkout";
      navigate(checkoutPath, {
        state: {
          message: {
            type: "success",
            text: "Payment method saved. Continue to checkout to finish subscribing.",
          },
        },
      });
      return;
    }

    // Redirect to dashboard with success message
    const basePath =
      userType === "freelancer" ? "/dashboard/freelancer" : "/dashboard";
    navigate(basePath, {
      state: {
        message: {
          type: "success",
          text: "Payment method saved successfully! You're all set for when your trial ends.",
        },
      },
    });
  };

  const handleCancel = () => {
    const basePath =
      userType === "freelancer" ? "/dashboard/freelancer" : "/dashboard";
    navigate(basePath);
  };

  // Calculate pricing before early returns
  const entityType = subscription?.entityType || "business";
  const isFreelancer = entityType === "freelancer";

  // Use locationCount directly from API response
  const locationCount = subscription?.locationCount || 1;
  const pricing = subscription?.pricing || {};
  const boothRenterCount = subscription?.boothRenterCount || 0;
  const boothRenterPrice = pricing.boothRenterMonthly || 0;

  // Calculate pricing based on entity type
  let basePrice, additionalPrice, boothRenterMonthlyTotal, totalMonthly;

  if (isFreelancer) {
    basePrice = pricing.freelancerMonthly || 40;
    additionalPrice = 0;
    boothRenterMonthlyTotal = 0;
    totalMonthly = basePrice;
  } else if (pricing.model === "per_professional" && pricing.plan) {
    // Per-professional pricing is computed per location by the server; the flat base no longer applies.
    basePrice = Number(pricing.plan.total || 0);
    additionalPrice = 0;
    boothRenterMonthlyTotal = boothRenterCount * boothRenterPrice;
    totalMonthly = basePrice + boothRenterMonthlyTotal;
  } else {
    basePrice = pricing.businessMonthly || 65;
    additionalPrice = pricing.businessAdditional || 45;
    boothRenterMonthlyTotal = boothRenterCount * boothRenterPrice;
    totalMonthly =
      basePrice +
      (locationCount - 1) * additionalPrice +
      boothRenterMonthlyTotal;
  }

  if (loading) {
    const content = (
      <div
        className="settings-page"
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: "40px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f4f6",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px",
          }}
        />
        <p style={{ color: "#6b7280" }}>Loading...</p>
      </div>
    );

    return <RoleAwareLayout>{content}</RoleAwareLayout>;
  }

  const content = (
    <div
      className="settings-page"
      style={{ maxWidth: "600px", margin: "0 auto", padding: "24px" }}
    >
      {/* Header */}
      <div style={{ marginBottom: "32px", textAlign: "center" }}>
        <button
          onClick={handleCancel}
          style={{
            background: "none",
            border: "none",
            color: "#3b82f6",
            fontSize: "14px",
            cursor: "pointer",
            padding: "8px",
            marginBottom: "16px",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          ← Back to Dashboard
        </button>

        <div
          style={{
            fontSize: "48px",
            marginBottom: "16px",
          }}
        >
          💳
        </div>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "700",
            margin: "0 0 12px",
            color: "#111827",
          }}
        >
          {isUpdate ? "Update Payment Method" : "Add Payment Method"}
        </h1>
        <p
          style={{
            fontSize: "15px",
            color: "#6b7280",
            margin: 0,
            lineHeight: "1.6",
          }}
        >
          {isUpdate
            ? "Update your payment information to continue your subscription."
            : "Add your payment method now. You won't be charged until your trial ends."}
        </p>
      </div>

      {/* Pricing Summary - Trial Active */}
      {!isUpdate && (
        <div
          style={{
            background: "white",
            border: "2px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "16px",
              fontWeight: "600",
              margin: "0 0 16px",
              color: "#111827",
            }}
          >
            📋 Subscription Details
          </h2>

          {isFreelancer ? (
            // Freelancer Pricing - Simple flat rate
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Freelancer Subscription
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginTop: "2px",
                  }}
                >
                  Full platform access
                </div>
              </div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                ${basePrice.toFixed(2)}/mo
              </div>
            </div>
          ) : (
            // Business Pricing - Base + Additional locations + booth renters
            <>
              {/* Base Price */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                    }}
                  >
                    Base Subscription
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    First location included
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  ${basePrice.toFixed(2)}/mo
                </div>
              </div>

              {/* Additional Locations */}
              {locationCount > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Additional Locations
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "2px",
                      }}
                    >
                      {locationCount - 1} × ${additionalPrice.toFixed(2)}/mo
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ${((locationCount - 1) * additionalPrice).toFixed(2)}/mo
                  </div>
                </div>
              )}

              {/* Booth Renters */}
              {boothRenterCount > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Active Booth Renters
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "2px",
                      }}
                    >
                      {boothRenterCount} x ${boothRenterPrice.toFixed(2)}/mo
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ${boothRenterMonthlyTotal.toFixed(2)}/mo
                  </div>
                </div>
              )}

              {/* Subtotal for businesses with multiple locations */}
              {(locationCount > 1 || boothRenterCount > 0) && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    borderTop: "1px solid #e5e7eb",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    Total When Trial Ends
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#111827",
                    }}
                  >
                    ${totalMonthly.toFixed(2)}/mo
                  </div>
                </div>
              )}
            </>
          )}

          {/* Today's Charge */}
          <div
            style={{
              marginTop: "16px",
              padding: "14px",
              background: "#f0fdf4",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#166534",
                }}
              >
                💰 Charge Today
              </div>
              {!isFreelancer && (locationCount > 1 || boothRenterCount > 0) && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#16a34a",
                    marginTop: "2px",
                  }}
                >
                  ${totalMonthly.toFixed(2)}/mo starts after trial
                </div>
              )}
            </div>
            <div
              style={{ fontSize: "24px", fontWeight: "700", color: "#10b981" }}
            >
              $0.00
            </div>
          </div>
        </div>
      )}

      {/* Update Mode Warning */}
      {isUpdate && (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              margin: "0 0 12px",
              color: "#92400e",
            }}
          >
            ⚠️ Payment Required
          </h3>
          <ul
            style={{
              fontSize: "14px",
              color: "#92400e",
              margin: 0,
              paddingLeft: "20px",
              lineHeight: "1.8",
            }}
          >
            <li>Your payment method will be updated immediately</li>
            <li>We'll retry the failed payment within 24 hours</li>
            <li>Your subscription will return to active status</li>
            <li>No interruption to your service</li>
          </ul>
        </div>
      )}

      {/* What Happens Box */}
      <div
        style={{
          background: "#fef3c7",
          border: "1px solid #fcd34d",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "32px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            margin: "0 0 12px",
            color: "#92400e",
          }}
        >
          {isUpdate ? "⚠️ Payment Required" : "ℹ️ What happens next?"}
        </h3>
        <ul
          style={{
            fontSize: "14px",
            color: "#92400e",
            margin: 0,
            paddingLeft: "20px",
            lineHeight: "1.8",
          }}
        >
          {isUpdate ? (
            <>
              <li>Your payment method will be updated immediately</li>
              <li>We'll retry the failed payment within 24 hours</li>
              <li>Your subscription will return to active status</li>
              <li>No interruption to your service</li>
            </>
          ) : (
            <>
              <li>
                <strong>No charge today</strong> - You're still in your free
                trial
              </li>
              <li>
                Card verified with a $0 authorization (released immediately)
              </li>
              <li>Billing starts automatically when you go live</li>
              <li>Cancel anytime before billing starts (no charge)</li>
            </>
          )}
        </ul>
      </div>

      {/* Payment Form (wrapped in Elements provider) */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "24px",
        }}
      >
        <Elements stripe={stripePromise}>
          <PaymentForm
            userType={userType}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        </Elements>
      </div>

      {/* Trust Badges */}
      <div
        style={{
          marginTop: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "24px",
          fontSize: "12px",
          color: "#9ca3af",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>🔒</span>
          <span>Secured by Stripe</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>✅</span>
          <span>PCI Compliant</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span>💎</span>
          <span>Cancel Anytime</span>
        </div>
      </div>
    </div>
  );

  return <RoleAwareLayout>{content}</RoleAwareLayout>;
};

export default PaymentMethodSetup;
